import './react-draft-wysiwyg.scss';
import { AtomicBlockUtils, ContentState, convertFromHTML, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import React from 'react';
import { Editor } from 'react-draft-wysiwyg';
import './style.scss';
import { IValidation } from 'src/types/main';

interface IProps {
  value?: string;
  customToolbar?: object;
  onChange: (text: string) => void;
  validation?: IValidation;
}

interface IState {
  editorState: EditorState;
}

export default class TextEditor extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const contentBlock = htmlToDraft(this.props.value || '');
    if (this.props.value && contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      this.state = {
        editorState,
      };
    } else {
      this.state = {
        editorState: EditorState.createEmpty(),
      };
    }
  }

  onEditorStateChange = (editorState: EditorState) => {
    this.setState({
      editorState,
    });
  };

  toolbar = () => {
    if (this.props.customToolbar) {
      return this.props.customToolbar;
    }
    return {
      options: ['blockType', 'inline', 'textAlign', 'list'],
      inline: {
        options: ['bold', 'italic', 'underline'],
        className: 'text-editor__inline',
      },
      list: {
        options: ['unordered', 'ordered'],
        className: 'text-editor__list',
      },
      textAlign: {
        options: ['left', 'center', 'right'],
        className: 'text-editor__text-align',
      },
      blockType: {
        inDropdown: true,
        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
        className: 'text-editor__block-type',
        dropdownClassName: 'text-editor__block-type__dropdown',
      },
    };
  };

  handlePastedText = (text: string, html: string | undefined, editorState: EditorState) => {
    if (html) {
      // Checking if the inserted HTML contains <img>
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const imgTag = doc.querySelector('img');

      if (imgTag) {
        const imgSrc = imgTag.getAttribute('src');
        if (imgSrc) {
          this.insertImage(imgSrc);
        }
      }

      // Processing text with formatting
      const contentBlock = convertFromHTML(html);
      const newContentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-fragment');

      this.onEditorStateChange(newEditorState);
      this.props.onChange(draftToHtml(convertToRaw(newEditorState.getCurrentContent())));
      return true; // Preventing default insert behavior
    }

    return false; // If there is no HTML, we use standard behavior
  };

  // Inserting an image into the editor
  insertImage = (src: string) => {
    const contentState = this.state.editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', { src });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      EditorState.push(this.state.editorState, contentStateWithEntity, 'apply-entity'),
      entityKey,
      ' '
    );

    this.onEditorStateChange(newEditorState);
    this.props.onChange(draftToHtml(convertToRaw(newEditorState.getCurrentContent())));
  };

  render() {
    const { editorState } = this.state;
    return (
      <>
        <Editor
          toolbar={this.toolbar()}
          editorState={editorState}
          editorStyle={{ lineHeight: '1.5' }}
          toolbarClassName="text-editor__header"
          onEditorStateChange={this.onEditorStateChange}
          handlePastedText={this.handlePastedText}
          editorClassName={`text-editor__editor ${!this.props.validation?.isValid ? 'has-error' : ''}`}
          onChange={() => {
            this.props.onChange(draftToHtml(convertToRaw(editorState.getCurrentContent())));
          }}
        />
        {!this.props.validation?.isValid && this.props.validation?.errorText && (
          <div className="input-error-message">{this.props.validation.errorText}</div>
        )}
      </>
    );
  }
}
